import React, { useState, useEffect, useCallback } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, ChartBarIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import DisclaimerFooter from './DisclaimerFooter';

export default function InsightsPage() {
  const [modelInsights, setModelInsights] = useState(null);
  const [rankInsights, setRankInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const navigate = useNavigate();

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch model performance
      try {
        const modelResponse = await axios.get(`${process.env.REACT_APP_API_URL}/insights/model-performance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Model Response:', modelResponse.data);
        console.log('Model Insights structure:', JSON.stringify(modelResponse.data, null, 2));
        setModelInsights(modelResponse.data);
      } catch (error) {
        console.error('Error fetching model insights:', error);
      }
      
      // Fetch rank movements
      try {
        const rankResponse = await axios.get(`${process.env.REACT_APP_API_URL}/insights/rank-movements`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Rank Response:', rankResponse.data);
        console.log('Rank Insights structure:', JSON.stringify(rankResponse.data, null, 2));
        setRankInsights(rankResponse.data);
      } catch (error) {
        console.error('Error fetching rank insights:', error);
      }
      
    } catch (error) {
      console.error('Error fetching insights:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsApproved(response.data.is_approved === true);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setIsApproved(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const formatReturn = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const color = value >= 0 ? 'text-green-400' : 'text-red-400';
    const sign = value >= 0 ? '+' : '';
    return <span className={color}>{sign}{value.toFixed(2)}%</span>;
  };

  const formatRankChange = (change) => {
    if (change === 0) return <span className="text-text-200">No change</span>;
    const color = change > 0 ? 'text-green-400' : 'text-red-400';
    const icon = change > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 inline mr-1" /> : <ArrowTrendingDownIcon className="h-4 w-4 inline mr-1" />;
    const sign = change > 0 ? '+' : '';
    return <span className={color}>{icon}{sign}{change}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-100"></div>
      </div>
    );
  }

  // Subscription banner for unapproved users
  const SubscriptionBanner = () => {
    if (isApproved) return null;
    
    return (
      <div className="mt-4 mb-6 bg-gradient-to-r from-primary-100/20 to-accent-100/20 rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0 md:mr-6">
            <h3 className="text-lg font-semibold text-text-100">
              <LockClosedIcon className="h-5 w-5 inline-block mr-2" />
              Unlock Full Access to Market Insights
            </h3>
            <p className="mt-1 text-text-200">
              Get exclusive insights on top-performing models, rank movements, and market trends across all securities.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center rounded-md bg-primary-100 px-4 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
            >
              Request Full Access
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO title="Insights" description="Market insights and signal-driven commentary from SharpeMetrix." />
      <div className="flex-grow bg-bg-100 px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-text-100">Insights</h1>
            <p className="mt-2 text-sm text-text-200">
              Discover top-performing models and significant rank movements in our proprietary ranking system.
            </p>
          </div>
        </div>

        <SubscriptionBanner />

        {/* Model Performance Insights */}
        {(isApproved) && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-text-100 mb-6 flex items-center">
            <TrophyIcon className="h-6 w-6 mr-2 text-primary-100" />
            Top Performing Models
          </h2>
          
          {modelInsights ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {modelInsights.insights && Object.entries(modelInsights.insights).map(([period, models]) => (
                <div key={period} className="bg-bg-200 rounded-lg shadow-sm border border-bg-300">
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-text-100 mb-4">{period} Performance</h3>
                    {models.length > 0 ? (
                      <div className="space-y-3">
                        {models.map((model, index) => (
                          <div 
                            key={model.model_id} 
                            className="flex items-center justify-between p-3 bg-bg-300 rounded-lg hover:bg-bg-400 cursor-pointer transition-colors"
                            onClick={() => navigate(`/model/${model.model_id}`, { 
                              state: { model: { id: model.model_id, model_name: model.model_name } } 
                            })}
                          >
                            <div className="flex items-center">
                              <span className="text-lg font-bold text-primary-100 mr-3">#{index + 1}</span>
                              <div>
                                <div className="font-medium text-text-100">{model.model_name || `Model ${model.model_id}`}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-text-200">Portfolio</div>
                              <div className="font-medium text-text-100">{formatReturn(model.ea_return)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-text-300">
                        <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                        <p>No data available for {period}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-text-300">
              <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
              <p>No model performance data available</p>
            </div>
          )}
        </div>)}

        {/* Rank Movement Insights */}
        {(isApproved) && (<div className="mt-12">
          <h2 className="text-xl font-semibold text-text-100 mb-6 flex items-center">
            <ArrowTrendingUpIcon className="h-6 w-6 mr-2 text-primary-100" />
            Rank Movements
          </h2>
          
          {rankInsights ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Top Improvements */}
              <div className="bg-bg-200 rounded-lg shadow-sm border border-bg-300">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-text-100 mb-4 flex items-center">
                    <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-green-400" />
                    Biggest Rank Improvements
                  </h3>
                  <div className="space-y-3">
                    {rankInsights.top_improvements.map((stock, index) => (
                      <div 
                        key={stock.security_id} 
                        className="flex items-center justify-between p-3 bg-bg-300 rounded-lg hover:bg-bg-400 cursor-pointer transition-colors"
                        onClick={() => navigate(`/ticker/${stock.security_id}`, { 
                          state: { security: stock } 
                        })}
                      >
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-green-400 mr-3">#{index + 1}</span>
                          <div>
                            <div className="font-medium text-text-100">{stock.ticker}</div>
                            <div className="text-sm text-text-200 truncate max-w-32">{stock.name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-text-200">Rank Change</div>
                          <div className="font-medium">{formatRankChange(stock.rank_change)}</div>
                          <div className="text-xs text-text-300">#{stock.current_rank}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Declines */}
              <div className="bg-bg-200 rounded-lg shadow-sm border border-bg-300">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-text-100 mb-4 flex items-center">
                    <ArrowTrendingDownIcon className="h-5 w-5 mr-2 text-red-400" />
                    Biggest Rank Declines
                  </h3>
                  <div className="space-y-3">
                    {rankInsights.top_declines.map((stock, index) => (
                      <div 
                        key={stock.security_id} 
                        className="flex items-center justify-between p-3 bg-bg-300 rounded-lg hover:bg-bg-400 cursor-pointer transition-colors"
                        onClick={() => navigate(`/ticker/${stock.security_id}`, { 
                          state: { security: stock } 
                        })}
                      >
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-red-400 mr-3">#{index + 1}</span>
                          <div>
                            <div className="font-medium text-text-100">{stock.ticker}</div>
                            <div className="text-sm text-text-200 truncate max-w-32">{stock.name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-text-200">Rank Change</div>
                          <div className="font-medium">{formatRankChange(stock.rank_change)}</div>
                          <div className="text-xs text-text-300">#{stock.current_rank}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-text-300">
              <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
              <p>No rank movement data available</p>
            </div>
          )}

          {/* Data as of date */}
          {rankInsights && (
            <div className="mt-6 text-center text-sm text-text-300">
              Data as of {new Date(rankInsights.current_date).toLocaleDateString()}
            </div>
          )}
        </div>)}

        {/* Enterprise CTA Section */}
        {!(isApproved) && (
          <div className="mt-8">
            <div className="rounded-lg bg-gradient-to-r from-primary-100/10 to-accent-100/10 p-8">
              <div className="mx-auto max-w-md text-center">
                <h2 className="text-xl font-semibold text-text-100">
                  Want Access to Premium Market Insights?
                </h2>
                <p className="mt-2 text-text-200">
                  Get exclusive insights on top-performing models, significant rank movements, sector trends, 
                  and custom analytics across over 25,000 securities.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                  >
                    Contact Us for Enterprise Access
                    <span className="ml-2" aria-hidden="true">&rarr;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
} 